import { Icono } from './Icono';
import type { NombreIcono } from './iconos';

/**
 * RV Segmentos (componentes.md §4): la barra de apartados de una pantalla (una sola por pantalla). Ordenador: píldora
 * blanca con la sombra de las tablas, botones de 44 en columnas iguales y el negro que se desliza al elegido. Móvil:
 * bloque `--rv-charcoal` con botones de 60 (icono 19 y texto corto 11/700) y el rojo que se desliza.
 *
 * Cifra de cada apartado (`cifra`, R-4, G139): se pinta si la pantalla la tiene (en ordenador: 11/700 en píldora
 * `--rv-paper`, blanca al 18 % sobre el elegido). Solo las cinco listas pequeñas del módulo 1 la traen (`total_count` de
 * Miembros, Invitaciones, Roles, Tablets y Facturas, `cifraDeLista`); las listas grandes no tienen total (G121): sin
 * cifra, nunca inventada. También en el móvil, arriba a la derecha del botón (G780: 1.9, 2.1, 4.4, 5.2…).
 */
export interface Segmento {
    etiqueta: string;
    corta?: string;
    icono: NombreIcono;
    cifra?: number | null;
}

export function Segmentos({ items, valor, onCambio, movil = false, etiqueta }: {
    items: readonly Segmento[];
    valor: number;
    onCambio: (i: number) => void;
    movil?: boolean;
    etiqueta: string;
}) {
    const n = Math.max(1, items.length);
    return (
        <div
            role="tablist"
            aria-label={etiqueta}
            className="rvs-caja"
            style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: `repeat(${String(n)}, minmax(0, 1fr))`,
                gap: movil ? 2 : 0,
                padding: 4,
                borderRadius: movil ? 16 : 999,
                background: movil ? 'var(--rv-charcoal)' : 'var(--rv-white)',
                boxShadow: movil ? 'none' : 'var(--rv-shadow-2)',
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    top: 4,
                    bottom: 4,
                    left: 4,
                    width: `calc((100% - 8px) / ${String(n)})`,
                    borderRadius: movil ? 12 : 999,
                    background: movil ? 'var(--rv-red)' : 'var(--rv-ink)',
                    transform: `translateX(${String(valor * 100)}%)`,
                    transition: 'transform 420ms cubic-bezier(0.16,1,0.3,1)',
                }}
            />
            {items.map((s, i) => {
                const on = i === valor;
                const cifra = s.cifra ?? null;
                return (
                    <button
                        key={s.etiqueta}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => {
                            onCambio(i);
                        }}
                        style={{
                            position: 'relative',
                            minWidth: 0,
                            minHeight: movil ? 60 : 44,
                            border: 'none',
                            background: 'transparent',
                            borderRadius: movil ? 12 : 999,
                            display: 'flex',
                            flexDirection: movil ? 'column' : 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: movil ? 4 : 8,
                            color: on ? '#fff' : movil ? 'rgba(255,255,255,0.6)' : 'var(--rv-ink)',
                            fontSize: movil ? (n > 4 ? 10 : 11) : 13,
                            fontWeight: movil ? 700 : 600,
                            cursor: 'pointer',
                            transition: 'color 300ms',
                        }}
                    >
                        {movil && <Icono nombre={s.icono} tamano={19} />}
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{movil ? (s.corta ?? s.etiqueta) : s.etiqueta}</span>
                        {cifra !== null && (
                            <span style={{ fontSize: movil ? 10 : 11, fontWeight: 700, padding: movil ? '0 6px' : '1px 7px', borderRadius: 999, background: movil ? (on ? 'rgba(255,255,255,0.25)' : 'var(--rv-red)') : on ? 'rgba(255,255,255,0.18)' : 'var(--rv-paper)', ...(movil ? { position: 'absolute', top: 6, right: 6, color: '#fff' } : {}) }}>{cifra}</span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
